import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  LoadingSpinner,
  ServerErrorFallbackCard,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  useAppSubscriptions,
  useCreateAppSubscription,
  useDeleteAppSubscription,
} from "@/lib/data/react-query/firm/firm-app-subscriptions";
import { useSearchPublisherApps } from "@/lib/data/react-query/firm/firm-publisher-apps";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type PublisherApp } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { useMemo, useState } from "react";

type FirmPublisherAppsViewMode = "library" | "my-apps";

function FirmPublisherAppsViewDangerous({
  mode,
}: {
  mode: FirmPublisherAppsViewMode;
}) {
  const { hasActionPermission } = useUser();
  const canConnectApps = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmAppSubscription,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDisconnectApps = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmAppSubscription,
    ACTION_PERMISSION_VALUES.delete,
  );
  const createSubscription = useCreateAppSubscription();
  const deleteSubscription = useDeleteAppSubscription();
  const {
    data: appSubscriptionsData,
    isLoading: isAppSubscriptionsLoading,
    isError: isAppSubscriptionsError,
  } = useAppSubscriptions();
  const {
    data: publisherAppsData,
    isLoading: isPublisherAppsLoading,
    isError: isPublisherAppsError,
  } = useSearchPublisherApps();
  const appSubscriptions = useMemo(
    () => appSubscriptionsData?.data ?? [],
    [appSubscriptionsData],
  );
  const publisherApps = useMemo(
    () => publisherAppsData?.data ?? [],
    [publisherAppsData],
  );
  const subscriptionByAppId = useMemo(
    () =>
      appSubscriptions.reduce<Record<string, string>>((acc, subscription) => {
        acc[subscription.appId] = subscription.id;
        return acc;
      }, {}),
    [appSubscriptions],
  );
  const activeApps = useMemo(
    () =>
      appSubscriptions.reduce<PublisherApp[]>((apps, subscription) => {
        const app =
          subscription.app ??
          publisherApps.find(
            (candidate) => candidate.id === subscription.appId,
          );
        if (app) apps.push(app);
        return apps;
      }, []),
    [appSubscriptions, publisherApps],
  );
  const availableApps = useMemo(
    () => publisherApps.filter((app) => !subscriptionByAppId[app.id]),
    [publisherApps, subscriptionByAppId],
  );
  const [pendingAppId, setPendingAppId] = useState<string | null>(null);

  if (isPublisherAppsError || isAppSubscriptionsError) {
    return <ServerErrorFallbackCard />;
  }

  const apps = mode === "library" ? availableApps : activeApps;
  const isLoading = isPublisherAppsLoading || isAppSubscriptionsLoading;
  const isLibrary = mode === "library";

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Typography tag="h2" variant="bold" size="xl" className="mb-0">
          {isLibrary ? "Available apps" : "Connected apps"}
        </Typography>
        <Badge variant="secondary">{apps.length}</Badge>
      </div>

      {isLoading ? (
        <div className="flex w-full justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : apps.length === 0 ? (
        <Card className="w-full border-2 border-dashed">
          <CardHeader className="space-y-1">
            <Typography
              variant="heading"
              className="text-muted-foreground text-center"
            >
              {isLibrary
                ? "No available apps right now."
                : "No connected apps yet."}
            </Typography>
            <Typography
              variant="default"
              className="text-muted-foreground text-center text-sm"
            >
              {isLibrary
                ? publisherApps.length > 0
                  ? "Your firm is already connected to every available app."
                  : "Approved apps will appear here when they become available."
                : canConnectApps
                  ? "Connect an app from the App Library to see it here."
                  : "No apps are connected to your firm."}
            </Typography>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {apps.map((app) => {
            const appName = app.name || "Unnamed app";
            const appTags = app.tags ?? [];
            const isAppPending = pendingAppId === app.id;

            return (
              <Card key={app.id} className="border-border w-full">
                <CardContent className="flex h-full flex-col p-6">
                  <div className="min-w-0 flex-1 space-y-2">
                    <Typography
                      variant="heading"
                      className="text-primary mb-0 text-lg"
                    >
                      {appName}
                    </Typography>
                    <Typography
                      className="text-muted-foreground text-sm"
                      variant="default"
                    >
                      {app.description || "No description provided."}
                    </Typography>
                  </div>
                  <div className="mt-5 flex flex-wrap items-end justify-between gap-3 border-t pt-4">
                    <div className="flex flex-1 flex-wrap gap-2">
                      {appTags.map((tag) => (
                        <Badge
                          key={`${app.id}-${tag}`}
                          variant="blue"
                          className="text-xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    {isLibrary
                      ? canConnectApps && (
                          <Button
                            type="button"
                            disabled={isAppPending}
                            className="relative"
                            onClick={() => {
                              setPendingAppId(app.id);
                              createSubscription.mutate(
                                { appId: app.id },
                                {
                                  onSettled: () => {
                                    setPendingAppId(null);
                                  },
                                },
                              );
                            }}
                          >
                            <span className={isAppPending ? "opacity-0" : ""}>
                              Connect
                            </span>
                            {isAppPending && (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <LoadingSpinner className="h-4 w-4" />
                              </span>
                            )}
                          </Button>
                        )
                      : canDisconnectApps && (
                          <Button
                            type="button"
                            variant="outline"
                            disabled={isAppPending}
                            className="relative"
                            onClick={() => {
                              const subscriptionId =
                                subscriptionByAppId[app.id];
                              if (!subscriptionId) return;
                              setPendingAppId(app.id);
                              deleteSubscription.mutate(
                                { subscriptionId },
                                {
                                  onSettled: () => {
                                    setPendingAppId(null);
                                  },
                                },
                              );
                            }}
                          >
                            <span className={isAppPending ? "opacity-0" : ""}>
                              Disconnect
                            </span>
                            {isAppPending && (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <LoadingSpinner className="h-4 w-4" />
                              </span>
                            )}
                          </Button>
                        )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function FirmPublisherAppsView({
  mode,
}: {
  mode: FirmPublisherAppsViewMode;
}) {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <FirmPublisherAppsViewDangerous mode={mode} />
    </ErrorBoundary>
  );
}
